/**
 * The point-of-collection privacy notice shown beside the signup field
 * (FR-016, contracts/web-surface.md §3). It is the disclosure a visitor reads
 * where they type; the full policy lives at `/privacy` (routes/privacy.tsx)
 * and reuses `removalSentence` below, so the wording lives here, unit-tested,
 * rather than inline in either component.
 *
 * Deliberately pure: the contact route is passed in, read from
 * `import.meta.env.VITE_REMOVAL_CONTACT` at the call site. That keeps both
 * branches testable and, more importantly, keeps the unconfigured branch
 * honest — when no contact is deployed the clause is dropped whole, so the
 * removal sentence still finishes as a sentence instead of trailing off into a
 * blank, a placeholder, or a broken `mailto:`.
 *
 * ## Wording
 *
 * Written in the first person plural with contractions, because the passive,
 * contraction-free register this used to be in ("Your email address is
 * collected…", "It is never shared…") reads like a policy document rather than
 * like the people who will actually be emailing you. `privacy-notice.test.ts`
 * pins the *promises* rather than the phrasing, so the wording can change as
 * long as every commitment survives: sole purpose, the beta, notification,
 * never shared, never sold, and removal on request.
 *
 * The contact goes inside the removal sentence rather than being appended as a
 * clause, so the unconfigured branch drops one preposition-free phrase and
 * still ends on "it" rather than on a dangling "to".
 */
const PURPOSE_SENTENCE =
  "We only use your address to tell you when the beta opens.";

const NON_DISCLOSURE_SENTENCE = "It's never shared or sold.";

/**
 * The removal promise as one finished sentence, with the contact inside it
 * when one is configured. Exported because the privacy policy page makes the
 * same promise — one implementation means the two can never phrase it apart.
 */
export function removalSentence(contact?: string): string {
  const contactRoute = contact?.trim() ?? "";

  return contactRoute.length > 0
    ? `Ask us at ${contactRoute} any time and we'll remove it.`
    : "Ask us any time and we'll remove it.";
}

export function buildPrivacyNotice(contact?: string): string {
  return [
    PURPOSE_SENTENCE,
    NON_DISCLOSURE_SENTENCE,
    removalSentence(contact),
  ].join(" ");
}
